import { useEffect, useMemo, useState } from "react";
import {
  AppShell,
  Group,
  Title,
  Select,
  SimpleGrid,
  Card,
  Stack,
  Text,
  Badge,
  Button,
  Center,
  Loader,
  Modal,
  Checkbox,
  SegmentedControl,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { IconFlame } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../shared/auth";
import { LoginPage } from "../admin/pages/Login";
import {
  useKdsStations,
  useKdsTickets,
  useStartTicket,
  useMarkReady,
  useMarkServed,
  useWasteKdsLine,
} from "../../shared/api";
import { useWebSocket } from "../../shared/ws";

const STATUS_LABEL: Record<string, string> = {
  NEW: "Bekliyor",
  PENDING: "Bekliyor",
  PREPARING: "Hazırlanıyor",
  READY: "Hazır",
  SERVED: "Servis Edildi",
};

// KdsTicketPriority — numerik (küçük = yüksek öncelik): 1=ACİL, 3=VIP, 5=Normal, 7=Düşük
const PRIORITY_COLOR: Record<number, string> = {
  1: "red",
  2: "red",
  3: "orange",
  5: "blue",
  7: "gray",
};

// Öncelik → KDS rozet metni. 2 = yakılan tabağın telafisi (remake);
// gerçek VIP (3) ile karışmasın diye ayrı "TELAFİ" etiketi.
const PRIORITY_LABEL: Record<number, string> = {
  1: "ACİL",
  2: "TELAFİ",
  3: "VIP",
};

function elapsedSec(created_at: string): number {
  return Math.floor((Date.now() - new Date(created_at).getTime()) / 1000);
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Aynı masanın bilet'lerini tek grup altında topla (intelligent batching) —
 *  şef masanın tüm tabaklarını birlikte görür/ateşler. Anahtar table_id;
 *  yoksa table_name; o da yoksa biletin kendisi (paket/gel-al gruplanmaz).
 *  Ekleme sırası korunur → biletler geliş sırasına göre listelenir. */
function groupByTable(
  tickets: any[],
): { key: string; tableName: string; items: any[] }[] {
  const map = new Map<string, { key: string; tableName: string; items: any[] }>();
  for (const t of tickets) {
    const key = String(
      t.table_id ?? t.table_name ?? t.ticket_id ?? t.id,
    );
    if (!map.has(key)) {
      map.set(key, { key, tableName: t.table_name || "Masa belirsiz", items: [] });
    }
    map.get(key)!.items.push(t);
  }
  return Array.from(map.values());
}

function TicketCard({
  ticket,
  onStart,
  onReady,
  onServed,
  onWaste,
  hideTable = false,
}: {
  ticket: any;
  onStart: (id: string) => void;
  onReady: (id: string) => void;
  onServed: (id: string) => void;
  onWaste: (ticket: any, line: any) => void;
  // Masa kartı içinde gruplanmışsa masa adı dışta gösterilir; kartta tekrar etme.
  hideTable?: boolean;
}) {
  // "Yaktım" yalnız hazırlık aşamasında anlamlı (PENDING/PREPARING).
  const canWaste = ["NEW", "PENDING", "PREPARING"].includes(ticket.status);
  const elapsed = elapsedSec(ticket.created_at);
  const isLate = elapsed > (ticket.warn_after_seconds || 300);
  return (
    <Card
      padding="md"
      radius="md"
      withBorder
      style={{
        borderColor: isLate ? "var(--mantine-color-red-6)" : undefined,
        borderWidth: isLate ? 3 : 1,
        animation: isLate ? "blink 1.2s ease-in-out infinite" : "none",
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" align="center" wrap="nowrap">
            {!hideTable && (
              <>
                <Text size="sm" c="dimmed" fw={500}>
                  🍽
                </Text>
                <Text fw={900} size="xl" lh={1.1}>
                  {ticket.table_name || "Masa belirsiz"}
                </Text>
              </>
            )}
            {ticket.priority && ticket.priority <= 3 && (
              <Badge
                size="sm"
                color={PRIORITY_COLOR[ticket.priority] || "blue"}
                variant="filled"
              >
                {PRIORITY_LABEL[ticket.priority] || "VIP"}
              </Badge>
            )}
          </Group>
          <Text fw={700} size="lg" {...(isLate ? { c: "red" } : {})}>
            ⏱ {formatElapsed(elapsed)}
          </Text>
        </Group>

        {(ticket.lines || []).map((l: any, i: number) => (
          <div key={i}>
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Text fw={700} size="xl">
                {l.quantity}× {l.product_name}
              </Text>
              {canWaste && (l.line_id || l.batched_line_ids?.length) && (
                <Tooltip label="Yaktım / fire (yeniden hazırla)">
                  <ActionIcon
                    color="red"
                    variant="light"
                    size="lg"
                    onClick={() => onWaste(ticket, l)}
                  >
                    <IconFlame size={20} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
            {(l.modifiers || []).map((m: any, j: number) => (
              <Text key={j} size="sm" c="yellow.5">
                + {m.name || m}
              </Text>
            ))}
            {l.notes && (
              <Text size="sm" c="dimmed" fs="italic">
                {l.notes}
              </Text>
            )}
          </div>
        ))}

        <Group gap="xs" mt="sm">
          {(ticket.status === "NEW" || ticket.status === "PENDING") && (
            <Button fullWidth size="md" color="orange" onClick={() => onStart(ticket.id)}>
              Başla
            </Button>
          )}
          {ticket.status === "PREPARING" && (
            <Button fullWidth size="md" color="green" onClick={() => onReady(ticket.id)}>
              Hazır
            </Button>
          )}
          {ticket.status === "READY" && (
            <Button fullWidth size="md" variant="default" onClick={() => onServed(ticket.id)}>
              Servis Edildi
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

export function App() {
  const { isAuthenticated, user, logout } = useAuth();
  const [station, setStation] = useState<string | null>(null);
  const { data: stationsData } = useKdsStations({ enabled: isAuthenticated });
  const stations: any[] = stationsData?.results || stationsData || [];

  // WS real-time push — yeni ticket veya status değişiminde anında refetch.
  // useKdsTickets'tan ÖNCE çağrılır: status'a göre polling açılır/kapanır.
  const qc = useQueryClient();
  const wsPath = station ? `/ws/kds/station/${station}/` : null;
  const { lastMessage, status: wsStatus } = useWebSocket<any>(wsPath);

  const { data: ticketsData, isLoading } = useKdsTickets(
    station ? { station, status: "NEW,PENDING,PREPARING,READY" } : undefined,
    { enabled: isAuthenticated, wsConnected: wsStatus === "OPEN" },
  );
  const tickets: any[] = useMemo(
    () => ticketsData?.results || ticketsData || [],
    [ticketsData],
  );

  const startTicket = useStartTicket();
  const markReady = useMarkReady();
  const markServed = useMarkServed();
  const wasteLine = useWasteKdsLine();

  // "Yaktım" modalı — aşçı satırı yaktığında sebep + kapsam + remake sorulur.
  const [wasteTarget, setWasteTarget] = useState<{
    ticketId: string;
    lineId: string;
    label: string;
    bomComponents: { ingredient_id: string; name: string }[];
  } | null>(null);
  const [wasteReason, setWasteReason] = useState("OVERCOOKED");
  const [wasteRemake, setWasteRemake] = useState(true);
  // "full" = tüm tabak fire | "partial" = sadece seçilen malzeme(ler)
  const [wasteMode, setWasteMode] = useState<"full" | "partial">("full");
  const [wasteIngredients, setWasteIngredients] = useState<string[]>([]);
  useEffect(() => {
    if (!lastMessage) return;
    qc.invalidateQueries({ queryKey: ["kds-tickets"] });
    if (lastMessage.event === "kds.ticket.routed") {
      const firstLine = (lastMessage.lines || [])[0];
      const itemSummary = firstLine
        ? `${firstLine.quantity}× ${firstLine.product_name}`
        : "";
      notifications.show({
        title: `🔔 Yeni sipariş — ${lastMessage.table_name || "Masa belirsiz"}`,
        message: itemSummary,
        color: "blue",
        autoClose: 4000,
      });
    }
  }, [lastMessage, qc]);

  function action(fn: any, msg: string) {
    return async (id: string) => {
      try {
        await fn.mutateAsync(id);
        notifications.show({ message: msg, color: "green", autoClose: 1500 });
      } catch (e: any) {
        notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
      }
    };
  }

  if (!isAuthenticated) return <LoginPage app="KDS" />;

  if (!station) {
    return (
      <Center h="100vh">
        <Card padding="xl" radius="lg" withBorder w={400}>
          <Stack gap="md">
            <Title order={2} ta="center">
              İstasyon Seç
            </Title>
            <Select
              label="Mutfak İstasyonu"
              data={stations.map((s: any) => ({ value: s.code || s.id, label: s.name || s.code }))}
              size="lg"
              value={station}
              onChange={setStation}
              placeholder="Bir istasyon seçin"
            />
          </Stack>
        </Card>
      </Center>
    );
  }

  const groups: Record<string, any[]> = {
    PENDING: tickets.filter((t) => t.status === "NEW" || t.status === "PENDING"),
    PREPARING: tickets.filter((t) => t.status === "PREPARING"),
    READY: tickets.filter((t) => t.status === "READY"),
  };

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Title order={3}>🍳 KDS — {station}</Title>
            <Badge size="lg">{user?.username}</Badge>
            <Badge color={wsStatus === "OPEN" ? "green" : "gray"} variant="dot">
              {wsStatus === "OPEN" ? "Canlı" : "Bağlanıyor..."}
            </Badge>
          </Group>
          <Group>
            <Button variant="default" onClick={() => setStation(null)}>
              İstasyon Değiştir
            </Button>
            <Button variant="light" color="red" onClick={logout}>
              Çıkış
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        {isLoading ? (
          <Center h={300}><Loader /></Center>
        ) : (
          <SimpleGrid cols={3} spacing="md">
            {(["PENDING", "PREPARING", "READY"] as const).map((status) => (
              <Stack key={status} gap="sm">
                <Group justify="space-between">
                  <Title order={4}>{STATUS_LABEL[status]}</Title>
                  <Badge size="lg" variant="light">
                    {groups[status].length}
                  </Badge>
                </Group>
                {groupByTable(groups[status]).map((g) => {
                  const onWaste = (ticket: any, line: any) => {
                    setWasteReason("OVERCOOKED");
                    setWasteRemake(true);
                    setWasteMode("full");
                    setWasteIngredients([]);
                    setWasteTarget({
                      ticketId: ticket.id,
                      lineId: line.line_id || (line.batched_line_ids || [])[0],
                      label: `${line.quantity}× ${line.product_name}`,
                      bomComponents: (line.bom_components || []).map((c: any) => ({
                        ingredient_id: c.ingredient_id,
                        name: c.name,
                      })),
                    });
                  };
                  const cards = g.items.map((t) => (
                    <TicketCard
                      key={t.ticket_id || t.id}
                      ticket={t}
                      hideTable={g.items.length > 1}
                      onStart={action(startTicket, "Hazırlamaya başlandı")}
                      onReady={action(markReady, "Hazır olarak işaretlendi")}
                      onServed={action(markServed, "Servis edildi")}
                      onWaste={onWaste}
                    />
                  ));
                  // Tek bilet → düz kart (mevcut görünüm). Çok bilet → aynı masa
                  // tek çerçeve altında, üstte masa başlığı + adet rozeti.
                  if (g.items.length === 1) return cards[0];
                  return (
                    <Card key={g.key} padding="sm" radius="md" withBorder
                      style={{ borderColor: "var(--mantine-color-dark-3)", borderWidth: 2 }}>
                      <Stack gap="xs">
                        <Group gap="xs" align="center" wrap="nowrap">
                          <Text size="sm" c="dimmed" fw={500}>🍽</Text>
                          <Text fw={900} size="xl" lh={1.1}>{g.tableName}</Text>
                          <Badge size="lg" color="blue" variant="light">
                            {g.items.length} sipariş
                          </Badge>
                        </Group>
                        {cards}
                      </Stack>
                    </Card>
                  );
                })}
                {groups[status].length === 0 && (
                  <Text c="dimmed" ta="center" py="xl">
                    Sipariş yok
                  </Text>
                )}
              </Stack>
            ))}
          </SimpleGrid>
        )}
      </AppShell.Main>

      {/* ===== "Yaktım" (fire + remake) modalı ===== */}
      <Modal
        opened={!!wasteTarget}
        onClose={() => { if (!wasteLine.isPending) setWasteTarget(null); }}
        title="Yaktım / Fire"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            <b>{wasteTarget?.label}</b> için fire kaydı. Kapsamı seç: tüm tabak
            mı, yoksa sadece bozulan malzeme mi?
          </Text>

          {/* Kapsam — yalnız reçeteli ürün (bom_components dolu) bileşen sunar */}
          {(wasteTarget?.bomComponents?.length ?? 0) > 0 && (
            <SegmentedControl
              fullWidth
              value={wasteMode}
              onChange={(v) => setWasteMode(v as "full" | "partial")}
              data={[
                { value: "full", label: "Tüm tabak" },
                { value: "partial", label: "Sadece malzeme" },
              ]}
            />
          )}

          {wasteMode === "partial" && (
            <Checkbox.Group
              label="Hangi malzeme(ler) bozuldu?"
              value={wasteIngredients}
              onChange={setWasteIngredients}
            >
              <Stack gap="xs" mt="xs">
                {wasteTarget?.bomComponents.map((c) => (
                  <Checkbox
                    key={c.ingredient_id}
                    value={c.ingredient_id}
                    label={c.name}
                  />
                ))}
              </Stack>
            </Checkbox.Group>
          )}

          <Select
            label="Sebep"
            value={wasteReason}
            onChange={(v) => v && setWasteReason(v)}
            data={[
              { value: "OVERCOOKED", label: "Yandı / Fazla Pişti" },
              { value: "DROPPED", label: "Düştü / Döküldü" },
              { value: "WRONG_ORDER", label: "Yanlış Hazırlandı" },
              { value: "SPOILAGE", label: "Bozuldu / SKT Geçti" },
              { value: "PREP_ERROR", label: "Hazırlık Hatası" },
              { value: "OTHER", label: "Diğer" },
            ]}
          />
          <Checkbox
            label={
              wasteMode === "partial"
                ? "Yeniden hazırla (yalnız seçilen malzeme)"
                : "Yeniden hazırla (remake — yeni tabak mutfağa)"
            }
            checked={wasteRemake}
            onChange={(e) => setWasteRemake(e.currentTarget.checked)}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setWasteTarget(null)}
              disabled={wasteLine.isPending}>
              Vazgeç
            </Button>
            <Button
              color="red"
              leftSection={<IconFlame size={16} />}
              loading={wasteLine.isPending}
              disabled={wasteMode === "partial" && wasteIngredients.length === 0}
              onClick={() => {
                if (!wasteTarget?.lineId) return;
                wasteLine.mutate(
                  {
                    ticketId: wasteTarget.ticketId,
                    line_id: wasteTarget.lineId,
                    reason_code: wasteReason,
                    remake: wasteRemake,
                    ...(wasteMode === "partial"
                      ? { wasted_ingredient_ids: wasteIngredients }
                      : {}),
                  },
                  {
                    onSuccess: (res: any) => {
                      notifications.show({
                        title: "Fire kaydedildi",
                        message: res?.partial
                          ? (res?.remake_ticket || wasteRemake
                              ? "Malzeme fire edildi + yeniden hazırlanıyor"
                              : "Malzeme fire edildi")
                          : res?.remake_ticket
                            ? "Stok düşüldü + yeniden hazırlanıyor"
                            : "Stok düşüldü",
                        color: "green",
                      });
                      setWasteTarget(null);
                    },
                    onError: (e: any) =>
                      notifications.show({
                        message: e?.response?.data?.detail || "Hata",
                        color: "red",
                      }),
                  },
                );
              }}
            >
              Fire Et
            </Button>
          </Group>
        </Stack>
      </Modal>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </AppShell>
  );
}
